import React from 'react';
import { get } from 'lodash';
import { connect } from 'react-redux';
import toast, { Toaster } from 'react-hot-toast';
import Loading from '../../../Global/Loading';
import TeacherSelections from '../../../Reports/containers/TeacherSelections';
import LanguageModal from '../blocks/LanguageModal';
import { getUserData } from '../../../../services/API';
import Tooltip from 'rc-tooltip';
import FaInfoCircle from 'react-icons/lib/fa/question-circle-o';
import FaEdit from "react-icons/lib/fa/edit";
import FaCheckCircle from "react-icons/lib/fa/check-circle-o";
import { ASSESSMENT_MODE_OPTIONS, ASSESSMENT_TASK, CONFIGURATION_DATA, SPANISH_MAPPING, RAN_OPTIONS, RAN_LABELS } from '../../../../constants/assessmentConfigPageConstants';
import { updateAssessmentConfigForSchool, updateAssessmentConfigForDistrict, getAssessmentConfig } from '../../../../services/amiraLicenseService';
import SavingConfigModal from '../../SavingConfigModal';
import SelectAssessmentTags, { combinedContentTags, createCombinedTagsFromSelectedAssessmentTag, findSelectedAssessmentTagFromSavedTags, findSelectedAssessmentTagObjectFromSavedTags, findSelectedRanTagFromSavedTags, getRanOverride, hasRanOverride } from './SchoolConfigurations/SelectAssessmentTags';
import ConfigurationCell from './SchoolConfigurations/ConfigurationTable/ConfigurationCell';
import cx from 'classnames';
import TaskConfigurationBanners from './SchoolConfigurations/ConfigurationBanners/TaskConfigurationBanners';
import Banners from '../blocks/Banners';
import {listAllCustomConfigurations, getCustomConfigurations} from './SchoolConfigurations/CustomConfigurations';
import RANOverrideMessageBanner from './SchoolConfigurations/SelectAssessmentTags/RANOverrideMessageBanner';
import { isRANOptionDisabled } from './SchoolConfigurations/SelectAssessmentTags/isRANOptionDisabled';

const TableHeader = () => {
  return (
    <thead className='configuration-table-header'>
      <tr>
        <th className='header-cell'>Task</th>
        <th className='header-cell'>Kinder</th>
        <th className='header-cell'>First</th>
        <th className='header-cell'>Second</th>
        <th className='header-cell'>Third</th>
        <th className='header-cell'>Fourth</th>
        <th className='header-cell'>Fifth</th>
        <th className='header-cell'>Sixth</th>
        <th className='header-cell'>Reading Rope Domains</th>
      </tr>
    </thead>
  )
}

const TableBody = ({ data, filter, onChange, contentTags }) => {
  return (
    <tbody className='config-table-body'>
      {data.filter(({ name }) => filter.includes(name)).map(({ name, description, configuration, reading_rope_domain }, taskIndex) =>
        <tr className='config-table-body-row' key={name}>
          <td className='config-table-body-data-cell'>
            <span>{name}</span>
            <Tooltip
              placement="right"
              trigger={['hover']}
              mouseEnterDelay={0.2}
              overlayStyle={{width: 500, background: 'white', opacity: 1 }}
              overlay={
                <div style={{ padding: 20, fontSize: 14 }}>
                  <div style={{ fontWeight: 'bold', marginBottom: 20 }}>{name}</div>
                  <div style={{ marginBottom: 10 }}>{description}</div>
                  {reading_rope_domain.map(({ name, icon }) => <img key={name} src={icon} style={{ width: 100, marginRight: 10 }} />)}
                </div>
              }
            >
              <FaInfoCircle size="20" />
            </Tooltip>
          </td>
          {configuration.map((cellData, configIndex) =>
            <ConfigurationCell key={name + configIndex} cellData={cellData} configIndex={configIndex} taskName={name} onChange={onChange} taskIndex={taskIndex} contentTags={contentTags} />
          )}
          <td className='config-table-body-data-cell'>
            {reading_rope_domain.map(({ name, icon }) => <img key={name} className='reading-rope-domain' src={icon} />)}
          </td>
        </tr>
      )}
    </tbody>
  )
}

class ConfigurationView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      practiceOnly: false,
      errorMsg: null,
      assessmentModeOptionIndex: 0,
      filter: ASSESSMENT_MODE_OPTIONS[0].filter,// Select Universal "filter" by default
      currentConfig: [],
      ranIndex: 0,
      languageTest: {},
      isSaveBtnEnabled: false,
    };

    this.saveConfig = this.saveConfig.bind(this);
    this.cancelConfig = this.cancelConfig.bind(this);
    this.updateConfig = this.updateConfig.bind(this);
    this.onLanguageSelected = this.onLanguageSelected.bind(this);
    this.openSaveConfigModal = this.openSaveConfigModal.bind(this);
    this.openEditLanguageModal = this.openEditLanguageModal.bind(this);
    this.onAssessmentTaskChange = this.onAssessmentTaskChange.bind(this);
    this.onAssessmentModeChange = this.onAssessmentModeChange.bind(this);
    this.onRanOptionChange = this.onRanOptionChange.bind(this);
    this.transformConfigToLicenses = this.transformConfigToLicenses.bind(this);
    this.applyTagOverrides = this.applyTagOverrides.bind(this);
  }

  componentDidMount(){
    const { userType, district, schools } = getUserData();

    if(userType == "DISTRICT_ADMIN" && district && this.props.selectedSchool){
      this.updateConfig(this.props.selectedSchool.id);
    } else if(userType != "DISTRICT_ADMIN") {
      this.updateConfig(schools[0].id);
    }
  }

  componentDidUpdate(prevProps) {
    if((!prevProps.selectedSchool && this.props.selectedSchool)
      || (this.props.selectedSchool && prevProps.selectedSchool && prevProps.selectedSchool.id !== this.props.selectedSchool.id)) {
      this.updateConfig(this.props.selectedSchool.id);
    }
  }

  async updateConfig(schoolId) {
    const districtId = getUserData().district.id;
    if(!this.state.loading) {
      this.setState({
        loading: true,
      });
    }
    getAssessmentConfig(districtId,schoolId)
      .then((data) => {
        if(!data) {
          this.setState({
            loading: false,
            practiceOnly: false,
            errorMsg: {
              title: "This school's configuration is not yet set up.",
              body: true,
            },
          });
          return;
        }

        const {
          contentTags,
          screenerEnabled,
          assessmentEnabled,
          nwfConfig,
          spellingConfig,
          vocabularyScreenerConfig,
          letterNamingAndSoundsConfig,
          listeningCompConfig,
          readingCompConfig
        } = data;

        const loadedConfig = {
          [ASSESSMENT_TASK.NWF]: nwfConfig,
          [ASSESSMENT_TASK.SPELLING]: spellingConfig,
          [ASSESSMENT_TASK.VOCABULARY]: vocabularyScreenerConfig,
          [ASSESSMENT_TASK.LETTER_NAMING_AND_SOUNDS]: letterNamingAndSoundsConfig,
          [ASSESSMENT_TASK.LISTENING_COMPREHENSION]: listeningCompConfig,
          [ASSESSMENT_TASK.READING_COMPREHENSION]: readingCompConfig,
        };

        // get the default configuration data and add custom configurations for the district
        let defaultConfigurationData = [...CONFIGURATION_DATA]
        const assessmentTag = findSelectedAssessmentTagFromSavedTags(contentTags);
        defaultConfigurationData = [...defaultConfigurationData, ...getCustomConfigurations(assessmentTag)]

        // if a tag other than amira default is selected, apply overrides from AssessmentConfigurationTags.json
        const currentConfig = defaultConfigurationData.map((config) => ({
          ...config,
          configuration: this.parseConfig(loadedConfig, config),
        }));

        //Looking up the initial RAN value based on the form overrides
        const defaultIndex = 0;

        if(!data.formOverrides) data.formOverrides = {dyslexia: [null,null]};
        const ranIndex = RAN_OPTIONS.findIndex((ranOption) => {
          const ranTag = findSelectedRanTagFromSavedTags(data.contentTags);
          return ranTag === ranOption.tag;
        });

        const initRanIndex = ranIndex !== -1 ? ranIndex : defaultIndex;

        const languageTest = data.spanishConfig ? SPANISH_MAPPING[data.spanishConfig] : SPANISH_MAPPING["BOTH_TWO_SESSIONS"];

        // If the district is configured as PRACTICE ONLY
        if(!assessmentEnabled && !screenerEnabled) {
          this.setState({
            loading: false,
            errorMsg: null,
            practiceOnly: true,
          })
        } else {
          const initFilter = ASSESSMENT_MODE_OPTIONS.filter(option=>option.assessmentEnabled === assessmentEnabled && option.screenerEnabled === screenerEnabled)[0];
          const assessmentModeOptionIndex = ASSESSMENT_MODE_OPTIONS.findIndex(option=>option.name === initFilter.name);

          this.setState({
            loading: false,
            errorMsg: null,
            formOverrides: data.formOverrides,
            practiceOnly: false,
            currentConfig,
            contentTags,
            languageTest,
            ranIndex: initRanIndex,
            filter: initFilter.filter,
            assessmentModeOptionIndex
          });
        }
      })
      .catch((err) => {
        this.setState({
          loading: false,
          practiceOnly: false,
          errorMsg: {
            title: 'We failed to fetch the configuration for this school.',
            body: false,
          },
        });

        console.error("Error loading School Config", err);
      });
  }

  parseConfig(loadedConfig, config) {
    if(loadedConfig[config.name]) {
      const overlappingConfig = config.configuration.slice(loadedConfig[config.name].length);
      return overlappingConfig.length > 0
        ? loadedConfig[config.name].concat(overlappingConfig)
        : loadedConfig[config.name];
    }

    return [...config.configuration];
  }

  applyTagOverrides(tag) {
    if (!tag || !tag.override) {
      return;
    }

    const updatedConfig = this.state.currentConfig.map(config => {
      const taskOverride = tag.override[config.name];
      if (taskOverride) {
        return {
          ...config,
          configuration: config.configuration.map((cellData, index) => {
            const value = taskOverride[index];
            if (!value) {
              return cellData;
            }else if(value === 1){ // Required
              return true; 
            }else if(value === 2){ // Banned
              return false; 
            }else if(value === 3){ // check box true
              return true; 
            }else{ // check box false
              return false; 
            }
          })
        };
      }
      return config;
    });

    // check if the tag has custom configuration available
    const customConfig = getCustomConfigurations(tag?.value);
    if(customConfig.length > 0){
      // add all custom configurations to the currentConfig
      customConfig.forEach((config) => {
        updatedConfig.push({
          ...config,
          configuration: config.configuration.map((cellData, index) => {
            return cellData;
          })
        });
      });
    }else{
      // remove all custom configurations from the currentConfig
      const allCustomConfigurations = listAllCustomConfigurations();
      allCustomConfigurations.forEach((config) => {
        const index = updatedConfig.findIndex((conf) => conf.name === config.name);
        if(index !== -1){
          updatedConfig.splice(index, 1);
        }
      });
    }

    let ranIndex = this.state.ranIndex;
    // if the tag has a ranIndex, update the ranIndex
    if(tag.ran_override){
      // if it is a string, find the index of the string in the RAN_OPTIONS array and use that index
      if(typeof tag.ran_override === 'string'){
        ranIndex = RAN_OPTIONS.findIndex((ranOption) => ranOption.tag === tag.ran_override);
        this.onRanOptionChange(ranIndex);
      }else if(Array.isArray(tag.ran_override)){
        const stateRAN = RAN_OPTIONS[this.state.ranIndex]?.tag;
        // update to a ran option in the override if the current ran option is not in the override
        if(!tag.ran_override.includes(stateRAN)){
          // find the index of the first element in the array and use that index
          ranIndex = RAN_OPTIONS.findIndex((ranOption) => ranOption.tag === tag.ran_override[0]);
          this.onRanOptionChange(ranIndex);
        }
      }
    }

    const combinedAssessmentAndRANTags = createCombinedTagsFromSelectedAssessmentTag(tag.value, RAN_OPTIONS[ranIndex]?.tag || RAN_OPTIONS[0].tag);
    const updatedContentTags = combinedContentTags(this.state.contentTags, combinedAssessmentAndRANTags);

    this.setState({
      currentConfig: updatedConfig,
      isSaveBtnEnabled: true,
      contentTags: updatedContentTags,
    });
  }

  transformConfigToLicenses() {
    let newConfig = {spanishConfig: this.state.languageTest.licenseTableName};

    newConfig.screenerEnabled = ASSESSMENT_MODE_OPTIONS[this.state.assessmentModeOptionIndex].screenerEnabled;
    newConfig.assessmentEnabled = ASSESSMENT_MODE_OPTIONS[this.state.assessmentModeOptionIndex].assessmentEnabled;

    const customConfiguration = getCustomConfigurations(this.state.contentTags);
    const allCustomConfigurations = listAllCustomConfigurations();
    if(!customConfiguration.length){
      // make all config keys from allCustomConfigurations null
      allCustomConfigurations.forEach((config) => {
        // make it null so that graphql can remove the config array for the configuration
        newConfig[config.licenseTableName] = null;
      });
    }

    this.state.currentConfig.map((config)=>{
      if(config.licenseTableName) {

        // map the configuration from object to boolean using the value property if it is an object
        config.configuration = config.configuration.map((cellData) => {
          if(typeof cellData === "object"){
            return cellData.value || false;
          }
          return cellData;
        });
        newConfig[config.licenseTableName] = config.configuration;
      }
    })

    const updatedContentTags = this.state.contentTags;
    newConfig.contentTags = updatedContentTags;
    newConfig.licensedContent = updatedContentTags;
    return newConfig;
  }

  onLanguageSelected(languageSelected) {
    let languageTest = JSON.parse(JSON.stringify(this.state.languageTest));
    for (let [key, value] of Object.entries(SPANISH_MAPPING)) {
      if(value.test === languageSelected.test && value.sequence.length === languageSelected.sequence.length && value.sequence[0] === languageSelected.sequence[0]){
        languageTest = value;
        break;
      }
    }

    this.setState({
      isSaveBtnEnabled: true,
      languageTest,
    }, this.props.closeModal);
  }

  openEditLanguageModal() {
    const selectedSchool = this.props.selectedSchool || getUserData().schools[0];
    this.props.openModal(
      <LanguageModal selectedSchool={selectedSchool} saveLanguageSelection={this.onLanguageSelected} languageSelected={this.state.languageTest.sequence} />,
      {
        closeButton: true,
        close: this.props.closeModal,
        className: "edit-language-modal"
      }
    );
  }

  onAssessmentModeChange(filter, assessmentModeOptionIndex) {
    this.setState({
      assessmentModeOptionIndex,
      filter,
      isSaveBtnEnabled: true,
    });
  }

  onRanOptionChange(ranIndex, disabled = false) {
    if(disabled) {
      return;
    }

    // update content tags with the newly selected RAN index
    const combinedAssessmentAndRANTags = createCombinedTagsFromSelectedAssessmentTag(findSelectedAssessmentTagFromSavedTags(this.state.contentTags), RAN_OPTIONS[ranIndex].tag);
    const updatedContentTags = combinedContentTags(this.state.contentTags, combinedAssessmentAndRANTags);

    this.setState({
      ranIndex,
      isSaveBtnEnabled: true,
      contentTags: updatedContentTags
    });
  }

  onAssessmentTaskChange(taskIndex, configIndex) {
    this.setState((prevState) => {
      let currentConfig = [ ...prevState.currentConfig ];
      currentConfig[taskIndex].configuration[configIndex] = !prevState.currentConfig[taskIndex].configuration[configIndex];

      return {
        isSaveBtnEnabled: true,
        currentConfig,
      };
    })
  }

  cancelConfig() {
    this.setState({
      languageTest: {},
      isSaveBtnEnabled: false,
    }, () => this.updateConfig(this.props.selectedSchool.id));
  }

  saveConfig(saveToDistrict = false) {
    const { selectedSchool, closeModal } = this.props;
    const { district, schools } = getUserData();
    let configToSave = this.transformConfigToLicenses();
    const districtId = district.id;
    const schoolId = selectedSchool.id;
    if(saveToDistrict) {
      updateAssessmentConfigForDistrict(districtId, schools, configToSave)
      .then((responses) => {
        const numberOfConfigSaved = responses.filter((res) => res).length
        this.setState({
          isSaveBtnEnabled: false,
        });
        closeModal();
        this.showToast(saveToDistrict, numberOfConfigSaved);
      });
    } else {
      updateAssessmentConfigForSchool(districtId, schoolId,configToSave)
      .then(() => {
        this.setState({
          isSaveBtnEnabled: false,
        });
        closeModal();
        this.showToast(saveToDistrict);
      });
    }
  }

  openSaveConfigModal() {
    if(!this.state.isSaveBtnEnabled) {
      return;
    }

    const selectedSchool = this.props.selectedSchool || getUserData().schools[0];
    this.props.openModal(
      <SavingConfigModal
        schoolName={selectedSchool.label}
        onSaveToSchool={() => this.saveConfig()}
        onSaveToDistrict={() => this.saveConfig(true)}
      />,
      {
        closeButton: true,
        close: this.props.closeModal,
      }
    );
  }

  showToast(saveToDistrict, numberOfConfigSaved = 1) {
    const { district } = getUserData();
    const target = saveToDistrict
      ? (<React.Fragment>district <strong>{district.label}</strong> ({numberOfConfigSaved} schools).</React.Fragment>)
      : (<strong>{this.props.selectedSchool.label}</strong>);

    toast.custom(
      <div style={{ backgroundColor: 'white', padding: 20, fontSize: 18, border: '1px solid #ccc', borderRadius: 10 }}>
        <div className='toastTitle' style={{ fontWeight: 'bold' }}>
          <FaCheckCircle size={24} color="green" style={{ marginRight: 10 }} />
          Changes Saved!
        </div>
        <div className='toastBody'>You've updated the assessment configuration for {target}</div>
      </div>,
      {
        duration: 3000,
      }
    );
  }

  renderLanguageBoxTitle() {
    const { languageTest }= this.state;
    const languageUI = {
      esp: 'Spanish',
      eng: 'English',
    };

    const { test, sequence } = languageTest;
    if(sequence && sequence.length >= 1) {
      return `${test} ${test == 1 ? 'Test' : 'Tests'}, ${languageUI[sequence[0]]} ${sequence.length > 1 ? `then ${languageUI[sequence[1]]}` : ''}`;
    }

    return 'No test selected.';
  }

  render () {
    const selectionProps = {
      isAggregateReport: true,
      hasScoreScaling: false,
      isSchoolReport: true,
      pageTitle: "School Configuration",
    };

    const { userType, schools, statePercentileCuts, district } = getUserData();
    const isDistrictAdmin = userType == "DISTRICT_ADMIN";
    const selectedSchool = this.props.selectedSchool || schools[0];
    const selectedAssessmentTagObject = findSelectedAssessmentTagObjectFromSavedTags(this.state.contentTags);
    const ranOverride = getRanOverride(findSelectedAssessmentTagFromSavedTags(this.state.contentTags));

    return (
      <div className="reports-root-container">
        <Toaster />
        <div className="reports-page-wrap">
          <div className='reports-page'>
          {isDistrictAdmin &&
            <TeacherSelections
              teacherSelectionTypes={selectionProps}
              selectedMetric={this.props.selectedMetric}
              selectedScale={this.props.selectedScale}
              selectedBenchmark={this.props.selectedBenchmark}
              selectedSchool={this.props.selectedSchool}
              history={this.props.history}
              isAdminReport={true}
            />
          }
          {this.state.loading && <Loading />}
          {!this.state.errorMsg && !this.state.loading && this.state.practiceOnly &&
            <div className="ConfigPage errorMsg">
              <p className="no-margin errorMsgTitle">{selectedSchool.label} is configured for practice only.</p>
              <p className="no-margin errorMsgBody">
                Contact your customer success manager or <a href='mailto:support@amiralearning.com'>support@amiralearning.com</a> to enable assessment.
              </p>
            </div>
          }
          {this.state.errorMsg && !this.state.loading && !this.state.practiceOnly &&
            <div className="ConfigPage errorMsg">
              <p className="no-margin errorMsgTitle">{this.state.errorMsg.title}</p>
              {this.state.errorMsg.body && <p className="no-margin errorMsgBody">Contact <a href='mailto:support@amiralearning.com'>support@amiralearning.com</a> for assistance.</p>}
            </div>
          }
          {!this.state.loading && !this.state.errorMsg && !this.state.practiceOnly &&
            <div className="ConfigPage">
              <div className='config-page-title'>Configure Assessment</div>
              <span className='config-page-description'>Choose which tasks to include in your district's assessment. Changes you make here will affect all students in your district.</span>
            {selectedSchool && selectedSchool.id && this.state.currentConfig.length > 0 &&
              <React.Fragment>
                <div className='assessment-mode-section'>
                  <div className='config-page-sub-title'>ASSESSMENT MODE</div>
                  <div className='assessment-mode-option-container'>
                    {ASSESSMENT_MODE_OPTIONS.map(({ name, description, filter, icon }, index) =>
                      <div className='assessment-mode-option' key={'assessment_mode_opt_' + name} onClick={() => this.onAssessmentModeChange(filter, index)}>
                        <input
                          type='radio'
                          name='assessment-mode'
                          className={'assessment-mode-radio-btn' + (index === this.state.assessmentModeOptionIndex ? ' selected' : '')}
                          checked={index === this.state.assessmentModeOptionIndex}
                          onChange={() => this.onAssessmentModeChange(filter, index)}
                        />
                        <img className='magnifier-icon' src={icon} alt="" />
                        <div className='option-right-side'>
                          <div className='assessment-mode-option-title'>{name}</div>
                          <span className='assessment-mode-option-description'>{description}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                    <div className='assessment-tags-section'>
                      <SelectAssessmentTags onTagChange={this.applyTagOverrides} savedAssessmentTags={this.state.contentTags} districtId={district?.id}/>
                    </div>
                <div className='assessment-tasks-section'>
                  <div className='config-page-sub-title'>ASSESSMENT TASKS</div>
                  <div className='configuration-table-wrapper'>
                    <table className='configuration-table'>
                      <TableHeader />
                      <TableBody data={this.state.currentConfig} filter={this.state.filter} onChange={this.onAssessmentTaskChange} contentTags={this.state.contentTags}/>
                    </table>
                  </div>
                </div>
                <TaskConfigurationBanners configState={this.state.currentConfig} taskFilter={this.state.filter}/>
                <div className='ran-option-section'>
                  <div className='config-page-sub-title'>RAN CONFIGURATION</div>
                  <span className='config-page-description'>Select the type of RAN task that Kindergarten and First Grade students will receive. All other students will get Number RAN.</span>
                    { ranOverride && selectedAssessmentTagObject && <RANOverrideMessageBanner ranOverride={ranOverride} selectedConfigTagLabel={selectedAssessmentTagObject.label} /> }
                    <div className='ran-option-container'>
                        {RAN_OPTIONS.map(({ name, description, icon, tag}, index) => {
                          const disabled = isRANOptionDisabled(ranOverride, tag);
                          const ranClassNames = cx('ran-option', {
                            'disabled': disabled
                          });
                          return <div className={ranClassNames} key={'ran_opt_' + name} onClick={() => this.onRanOptionChange(index, disabled)}>
                            <input
                              disabled={disabled}
                              type='radio'
                              name='ran-opt'
                              className={'ran-mode-radio-btn' + (index === this.state ? ' selected' : '')}
                              checked={index === this.state.ranIndex}
                              onChange={() => this.onRanOptionChange(index, disabled)}
                            />
                            <img className='ran-icon' src={icon} alt="" />
                            <div className='option-right-side'>
                              <div className='ran-option-title'>{name}</div>
                              <span className='ran-option-description'>{description}</span>
                            </div>
                          </div>
                        }
                        )}
                  </div>
                </div>
                <div className='language-configuration-section'>
                  <div className='config-page-sub-title'>LANGUAGE CONFIGURATION</div>
                  <span className='config-page-description'>Indicate whether students designated as bilingual should get English, Spanish, or both. If both, select which assessment should come first and whether students should take them in a single session or in separate sessions.</span>
                  <div className='language-config-box'>
                    <div className='language-selection-description'>{this.renderLanguageBoxTitle()}</div>
                    <div className='language-selection-right-side'>
                      <div className='language-btns'>
                      {Array.isArray(this.state.languageTest.sequence) &&
                        <React.Fragment>
                          {this.state.languageTest.sequence[0] && <span className={this.state.languageTest.sequence.length == 1 ? 'language-pill' : 'language-pill double'}>{this.state.languageTest.sequence[0].toUpperCase()}</span>}
                          {this.state.languageTest.sequence[1] && <span className='language-pill last-pill'>{this.state.languageTest.sequence[1].toUpperCase()}</span>}
                        </React.Fragment>
                    }
                      </div>
                      <div className='config-page-language-edit' onClick={this.openEditLanguageModal}>
                        <FaEdit size="20" style={{ marginRight: 10 }} />
                        Edit
                      </div>
                    </div>
                  </div>
                </div>
                <div className='config-page-control-btns'>
                  <div className='config-cancel-btn' onClick={this.cancelConfig}>Cancel</div>
                  <div className={'config-save-btn' + (this.state.isSaveBtnEnabled ? '' : ' disabled')} onClick={this.openSaveConfigModal}>Save</div>
                </div>
              </React.Fragment>
            }
            </div>
          }
          </div>
        </div>
      </div>
    )
  }
}

function mapStateToProps(state){
  return {
    selectedSchool: get(state, 'reports.roster.ui.selections.school', null),
  }
}

 export default connect(mapStateToProps)(ConfigurationView);